import { Controller } from "@hotwired/stimulus"
import { createGrid, ModuleRegistry, AllCommunityModule, themeQuartz } from "ag-grid-community"
import behaviors from "../grid_behaviors"
import * as clipboard from "../grid/clipboard"
import * as fillHandle from "../grid/fill_handle"
import * as recordSync from "../grid/record_sync"
import * as totals from "../grid/totals"

ModuleRegistry.registerModules([AllCommunityModule])

const compactGridTheme = themeQuartz.withParams({
  cellHorizontalPaddingScale: 0.5,
  rowVerticalPaddingScale: 0.5
})

// Generic AG Grid controller backed by <template> targets for data.
//
// Each row is a <template data-grid-target="row" data-row-id="123"> element
// whose data-* attributes carry the field values. id is the immutable AG Grid
// row key. Persisted rows also have recordId; browser-created rows retain their
// temporary id after recordId is assigned. The grid
// renders into a separate container target so AG Grid's DOM doesn't
// interfere with the row data elements.
//
// Turbo Streams can append/remove/replace these elements and the grid
// reacts automatically via Stimulus target lifecycle callbacks:
//
//   - append → rowTargetConnected adds the row to the grid
//   - remove → rowTargetDisconnected removes it (deferred one frame)
//   - replace → disconnect + connect reconciles: the deferred removal
//     is cancelled and the existing grid row is updated in place
//
// Grid edits are synced back to the template's data-row-* attributes
// so the DOM always reflects the current state.
//
// Column behaviors:
//   Column defs can declare a `behavior` key referencing a registered
//   behavior module. Behaviors produce AG Grid colDef callbacks
//   (valueFormatter, onCellValueChanged, etc.) that get merged in.
//
//   Column def:  { field: "vin", behavior: "vinLookup" }
//   Config:      data-grid-behaviors-value='{"vinLookup":{"url":"/api/vehicles"}}'
//
// Usage:
//   <div data-controller="grid"
//        data-grid-column-defs-value='[{"field":"vin","behavior":"vinLookup"}]'
//        data-grid-behaviors-value='{"vinLookup":{"url":"/api/vehicles"}}'>
//
//     <div data-grid-target="container"></div>
//
//     <template data-grid-target="row" data-row-id="1"
//               data-row-vin="1HGCM82633A004352">
//     </template>
//   </div>
//
// Events dispatched:
//   grid:cellChanged      — { field, oldValue, newValue, data }
//   grid:selectionChanged — { selectedIds } database record IDs (when selectable is true)
//
// Public API:
//   updateRow(id, fields) — merge fields into an existing row
export default class extends Controller {
  static targets = ["container", "row"]

  static values = {
    columnDefs: Array,
    behaviors: { type: Object, default: {} },
    selectable: { type: Boolean, default: false },
    addable: { type: Boolean, default: true }
  }

  connect() {
    this.emptyRowCounter = 0
    this.pendingRemovals = {}

    this.gridApi = createGrid(this.containerTarget, this.gridOptions)
    this.gridApi.addEventListener("cellValueChanged", this.onCellValueChanged.bind(this))
    this.gridApi.addEventListener("cellEditingStarted", this.onCellEditingStarted.bind(this))

    if (this.selectableValue) {
      this.gridApi.addEventListener("selectionChanged", this.onSelectionChanged.bind(this))
    }

    clipboard.install(this)
    fillHandle.install(this)
    totals.install(this)
    recordSync.install(this)

    this.autoSizeMarkedColumns()
  }

  disconnect() {
    // Cancel any deferred removals
    Object.values(this.pendingRemovals).forEach((id) => cancelAnimationFrame(id))
    this.pendingRemovals = {}

    clipboard.uninstall(this)
    fillHandle.uninstall(this)
    totals.uninstall(this)
    recordSync.uninstall(this)

    if (this.gridApi) {
      this.gridApi.destroy()
      this.gridApi = null
    }
  }

  // ── Public API ───────────────────────────────────────────

  // Set validation errors on a row. `errors` is an object like
  // { vin: "must be exactly 17 characters" }. Pass null values to clear
  // specific fields, or use `source` to manage errors by origin.
  setRowErrors(id, errors) {
    if (!this.gridApi) return

    const rowNode = this.gridApi.getRowNode(String(id))
    if (!rowNode) return

    const existing = rowNode.data._errors || {}
    const merged = { ...existing }

    for (const [field, message] of Object.entries(errors)) {
      if (message) {
        merged[field] = message
      } else {
        delete merged[field]
      }
    }

    rowNode.data._errors = merged
    this.gridApi.refreshCells({ rowNodes: [rowNode], force: true })
  }

  // Update fields on an existing row by grid ID.
  // Dispatches cellChanged so listeners (e.g. autosave) react to
  // programmatic updates like VIN decode.
  updateRow(id, fields) {
    if (!this.gridApi) return

    const rowNode = this.gridApi.getRowNode(String(id))
    if (!rowNode) return

    Object.assign(rowNode.data, fields)
    this.gridApi.applyTransaction({ update: [rowNode.data] })
    this.gridApi.refreshCells({ rowNodes: [rowNode], force: true })
    this.refreshTotals()
    this.syncRowToDOM(id, fields)

    this.dispatch("cellChanged", {
      detail: { field: null, oldValue: null, newValue: null, data: rowNode.data }
    })
  }

  // ── Column defs with behaviors ───────────────────────────

  // Merge behavior-produced colDef callbacks into column definitions.
  // Strips the `behavior` key so AG Grid doesn't see it.
  get resolvedColumnDefs() {
    const updateRow = this.updateRow.bind(this)

    return this.columnDefsValue.map((colDef) => {
      const { behavior, aggregate, autoSize, requiresField, fillable, ...agColDef } = colDef

      // Column is only editable when the required field has a value
      if (requiresField) {
        agColDef.editable = (params) => {
          if (params.node.rowPinned) return false
          const val = params.data?.[requiresField]
          return val !== undefined && val !== null && String(val).trim() !== ""
        }
      }

      if (!behavior) return agColDef

      const handler = behaviors[behavior]
      if (!handler) {
        console.warn(`Grid: unknown behavior "${behavior}" on field "${colDef.field}"`)
        return agColDef
      }

      const config = this.behaviorsValue[behavior] || {}
      const overrides = handler.apply(config, updateRow)

      return { ...agColDef, ...overrides }
    })
  }

  // ── Target lifecycle ─────────────────────────────────────

  rowTargetConnected(element) {
    if (!this.gridApi) return

    const rowData = this.parseRow(element)
    const incomingGridId = String(rowData.id)
    const replacesRowId = element.dataset.replacesRowId
    let existing = replacesRowId ? this.gridApi.getRowNode(replacesRowId) : null

    existing ||= this.gridApi.getRowNode(incomingGridId)
    if (!existing && rowData.recordId) {
      this.gridApi.forEachNode((node) => {
        if (!existing && String(node.data?.recordId) === String(rowData.recordId)) existing = node
      })
    }

    if (!existing) {
      this.cancelPendingRemoval(incomingGridId)
      this.gridApi.applyTransaction({ add: [rowData] })
      this.refreshTotals()
      return
    }

    const gridRowId = String(existing.data.id)
    this.cancelPendingRemoval(gridRowId)
    element.dataset.rowId = gridRowId

    if (replacesRowId) {
      const serverRenderedFields = Object.fromEntries(
        Object.entries(rowData).filter(([field]) => field.startsWith("__") && field.endsWith("Html"))
      )
      Object.assign(existing.data, serverRenderedFields, { recordId: rowData.recordId })
      this.syncRowToDOM(gridRowId, existing.data)
    } else {
      Object.assign(existing.data, rowData, { id: gridRowId })
    }

    existing.updateData(existing.data)
    this.gridApi.refreshCells({ rowNodes: [existing], force: true })
    this.refreshTotals()
  }

  cancelPendingRemoval(id) {
    const animationFrame = this.pendingRemovals[id]
    if (!animationFrame) return

    cancelAnimationFrame(animationFrame)
    delete this.pendingRemovals[id]
  }

  rowTargetDisconnected(element) {
    const id = element.dataset.rowId
    if (!id || !this.gridApi) return

    this.pendingRemovals[id] = requestAnimationFrame(() => {
      delete this.pendingRemovals[id]
      const rowNode = this.gridApi?.getRowNode(id)
      if (rowNode) {
        this.gridApi.applyTransaction({ remove: [rowNode.data] })
        this.refreshTotals()
      }
    })
  }

  // ── Grid setup ───────────────────────────────────────────

  get gridOptions() {
    const opts = {
      theme: compactGridTheme,
      columnDefs: this.resolvedColumnDefs,
      rowData: this.initialRowData,
      getRowId: (params) => String(params.data.id),
      popupParent: document.body,
      pinnedBottomRowData: totals.initialPinnedData(this),
      defaultColDef: {
        // Template dataset values start as strings, while editors and VIN decode
        // can produce numbers. Our column behaviors normalize values themselves.
        cellDataType: false,
        editable: (params) => !params.node.rowPinned,
        sortable: true,
        filter: true,
        resizable: true,
        suppressKeyboardEvent: this.suppressKeyboardEvent.bind(this),
        cellClass: (params) => {
          if (params.node.rowPinned) return null
          const colEditable = params.colDef.editable
          const isEditable =
            typeof colEditable === "function" ? colEditable(params) : colEditable !== false
          const classes = [isEditable ? "ag-cell--editable" : "ag-cell--readonly"]
          // Columns with cellStyle or cellRenderer manage their own presentation — opt out of readonly dimming
          if (params.colDef.cellStyle || params.colDef.cellRenderer) classes.push("ag-cell--colored")
          return classes
        },
        cellClassRules: {
          "ag-cell--error": (params) => {
            if (params.node.rowPinned) return false
            const field = params.colDef.field
            return field && !!params.data?._errors?.[field]
          }
        },
        tooltipValueGetter: (params) => {
          if (params.node.rowPinned) return null
          const field = params.colDef.field
          return field && params.data?._errors?.[field]
        }
      },
      enterNavigatesVerticallyAfterEdit: true,
      stopEditingWhenCellsLoseFocus: true,
      domLayout: "autoHeight",
      tooltipShowDelay: 300
    }

    if (this.selectableValue) {
      opts.rowSelection = {
        mode: "multiRow",
        checkboxes: true,
        headerCheckbox: true,
        isRowSelectable: (node) => {
          if (node.rowPinned) return false

          const recordId = String(node.data?.recordId || "")
          const batchSelection = this.element.closest('[data-controller~="batch-selection"]')
          const checkboxes = batchSelection?.querySelectorAll('[data-batch-selection-target~="checkbox"]') || []
          return recordId !== "" && Array.from(checkboxes).some((checkbox) => checkbox.value === recordId)
        }
      }
      opts.selectionColumnDef = { pinned: "left" }
    }

    return opts
  }

  // Override Tab while editing so it navigates to the next cell WITHOUT
  // entering edit mode. AG Grid's default starts editing the next cell,
  // which prevents arrow-key navigation.
  suppressKeyboardEvent(params) {
    if (!params.editing || params.event.key !== "Tab") return false

    params.event.preventDefault()
    params.api.stopEditing(false)

    const columns = params.api.getAllDisplayedColumns()
    const currentColIndex = columns.indexOf(params.column)
    const forward = !params.event.shiftKey

    let nextColIndex = forward ? currentColIndex + 1 : currentColIndex - 1
    let nextRowIndex = params.node.rowIndex

    // Wrap to next/previous row when we run past the last/first column
    if (nextColIndex >= columns.length) {
      nextColIndex = 0
      nextRowIndex += 1
    } else if (nextColIndex < 0) {
      nextColIndex = columns.length - 1
      nextRowIndex -= 1
    }

    if (nextRowIndex >= 0 && nextRowIndex < params.api.getDisplayedRowCount()) {
      params.api.setFocusedCell(nextRowIndex, columns[nextColIndex])
    }

    return true
  }

  // Parse all row targets already in the DOM at connect time.
  // When addable is true (default), appends an empty row for new entry.
  get initialRowData() {
    const saved = this.rowTargets.map((t) => this.parseRow(t))
    if (this.addableValue) {
      saved.push({ id: this.nextEmptyId() })
    }
    return saved
  }

  nextEmptyId() {
    return `empty-${this.emptyRowCounter++}`
  }

  // ── Auto-sizing ───────────────────────────────────────────

  get autoSizeFields() {
    return this.columnDefsValue.filter((c) => c.autoSize).map((c) => c.field)
  }

  autoSizeMarkedColumns() {
    const fields = this.autoSizeFields
    if (!fields.length || !this.gridApi) return
    this.gridApi.autoSizeColumns(fields)
  }


  // ── DOM sync ─────────────────────────────────────────────

  syncRowToDOM(id, fields) {
    const el = this.rowTargets.find((t) => t.dataset.rowId === String(id))
    if (!el) return

    for (const [field, value] of Object.entries(fields)) {
      // Skip internal fields (e.g. _errors, __actionsHtml)
      if (field.startsWith("_")) continue
      // "vin" → "rowVin", "purchasePrice" → "rowPurchasePrice"
      const datasetKey = "row" + field.charAt(0).toUpperCase() + field.slice(1)
      el.dataset[datasetKey] = value ?? ""
    }
  }

  // ── Helpers ──────────────────────────────────────────────

  // Extract data-row-* attributes into a plain object.
  // Also extracts HTML content from named slots inside the element.
  parseRow(element) {
    const data = {}
    for (const [key, value] of Object.entries(element.dataset)) {
      if (key.startsWith("row") && key !== "rowTarget") {
        // "rowVin" → "vin", "rowPurchasePrice" → "purchasePrice"
        const field = key.charAt(3).toLowerCase() + key.slice(4)
        data[field] = value
      }
    }

    // Extract HTML from data-slot elements.
    // <template> children live in .content (a DocumentFragment).
    const slotRoot = element.content || element
    slotRoot.querySelectorAll("[data-slot]").forEach((el) => {
      data[`__${el.dataset.slot}Html`] = el.innerHTML
    })

    return data
  }

  // When the user starts editing the last row, append a new empty row
  // so there's always a blank row ready for the next entry.
  onCellEditingStarted(event) {
    if (!this.gridApi || !this.addableValue) return

    const lastDataRowIndex = this.gridApi.getDisplayedRowCount() - 1

    if (event.rowIndex === lastDataRowIndex) {
      this.gridApi.applyTransaction({ add: [{ id: this.nextEmptyId() }] })
    }
  }

  onSelectionChanged() {
    if (!this.gridApi) return

    const selectedIds = this.gridApi
      .getSelectedRows()
      .map((row) => String(row.recordId || ""))
      .filter(Boolean)

    this.dispatch("selectionChanged", { detail: { selectedIds } })
  }

  onCellValueChanged(event) {
    // Invoke the colDef-level callback (from behaviors) — the grid-level
    // handler takes precedence in AG Grid, so we call it manually.
    event.colDef.onCellValueChanged?.(event)

    // Refresh all cells in the row so derived formatting updates.
    if (event.node) {
      this.gridApi.refreshCells({ rowNodes: [event.node], force: true })
    }

    this.refreshTotals()

    // Keep the hidden row element in sync with the grid
    if (event.colDef.field && event.data?.id) {
      this.syncRowToDOM(event.data.id, { [event.colDef.field]: event.newValue })
    }

    this.dispatch("cellChanged", {
      detail: {
        field: event.colDef.field,
        oldValue: event.oldValue,
        newValue: event.newValue,
        data: event.data
      }
    })
  }
}
